// El perfil institucional del TENS (plan del mundo TENS, PR2, 2026-10-04).
//
// Hasta ahora MiPAE TENS estaba hecho a la medida de una sola unidad, la UCI de
// Bupa: camas 213 a 233, rotación del catéter urinario cada 2 horas, ventana de
// 6 horas para evolucionar. Eso no se borra, pero deja de ser "lo del TENS" y
// pasa a ser UN perfil institucional que se activa SOLO si la institución del
// perfil de la persona es Clínica Bupa (decisión del fundador, 2026-10-04: "lo
// de Bupa no eliminarlo, dejarlo en una rama aparte solo para Bupa porque tiene
// que ver con el estudio").
//
// Lo que Bupa prende es exactamente cinco cosas, todas de interfaz:
//  1. el pizarrón de camas 213 a 233 como atajo,
//  2. el reloj de rotación del catéter urinario (cada 120 minutos),
//  3. la nota de la ventana de 6 horas para evolucionar,
//  4. el recibo del turno en lista (conversar con la enfermera, revisar el kárdex), que
//     fuera de Bupa es una sugerencia plegable (decisión del fundador, 2026-10-04),
//  5. la visita "con la enfermera": el examen en conjunto, que fuera de Bupa es la visita
//     propia del técnico y su evolución no dice que se hizo junto a nadie.
// La MÉTRICA DEL ESTUDIO no cuelga de la institución: sigue gateada por el
// consentimiento (lib/estudio.js) y por el reloj de la rotativa
// (lib/turnoEstudio.js). Un doble gate sería un bug esperando.
//
// Dominio puro. `tens.js` no importa nada y así se queda: este archivo lo
// importa a él, nunca al revés.
import { esInstitucionBupa } from '../data/instituciones';
import { CAMAS_UPCA, VENTANA_EVOLUCION_TENS_MIN } from './tens';
import { sanitizarCama } from './entregaTurno';

// Kill-switch ON por defecto (norma del repo). Apagado, TODO el mundo se
// comporta como Bupa y como UCI, o sea exactamente como antes de esta ola.
export function tensServiciosHabilitado() {
  return process.env.REACT_APP_TENS_SERVICIOS_ENABLED !== 'false';
}

// Kill-switch ON por defecto del hub de un toque (PR3): la tira "Lo que viene",
// el botón "Registrar" de cada paciente y la hoja de registro rápido. Apagado, el
// hub es el de siempre; los riesgos, los signos vitales y la entrega no dependen
// de él.
export function tensHubRapidoHabilitado() {
  return process.env.REACT_APP_TENS_HUB_RAPIDO_ENABLED !== 'false';
}

// El pizarrón vive en tens.js (dos tests lo importan de ahí y tens.js no debe
// importar nada); acá solo se re-exporta con el nombre que dice de quién es.
export const CAMAS_BUPA_UPCA = CAMAS_UPCA;

const ID_BUPA = 'bupa_upca';

/**
 * ¿A qué institución con perfil propio pertenece la persona? Hoy solo existe
 * una. Sin institución, o con una que no tiene perfil, devuelve null.
 * @param {object|null} profile el perfil REAL (nunca el del mundo visitado)
 * @returns {'bupa_upca'|null}
 */
export function institucionTens(profile) {
  if (!tensServiciosHabilitado()) return ID_BUPA;
  // Cualquier grafía que nombre a Bupa (2026-10-06: había TENS con "BUPA
  // Santiago" o "Clínica BUPA, Servicio UCI" que el alias exacto dejaba fuera).
  return esInstitucionBupa(profile?.institucion) ? ID_BUPA : null;
}

const SIN_RASGOS = Object.freeze({ pizarron: null, rotacionCupMin: null, ventanaEvolucionMin: null, reciboEnLista: null, visitaConEnfermera: null });

// Exactamente cinco claves, a propósito: lo que una institución activa es una
// lista corta y cerrada, y un sexto rasgo es una decisión, no un descuido.
// El cuarto (`reciboEnLista`, 2026-10-04) lo decidió el fundador al ver la ficha: recibir el
// turno con su lista (conversar con la enfermera, revisar el kárdex) es la rutina de la UCI de
// Bupa y no se aplica literal en todos lados. Fuera de Bupa es una sugerencia plegable.
// El quinto (`visitaConEnfermera`) es la tercera pieza de esa misma rutina: la visita en
// conjunto. Su prosa no puede afirmar "junto a la enfermera" donde nadie lo constató.
const RASGOS = Object.freeze({
  [ID_BUPA]: Object.freeze({
    pizarron: CAMAS_BUPA_UPCA,
    rotacionCupMin: 120,
    ventanaEvolucionMin: VENTANA_EVOLUCION_TENS_MIN,
    reciboEnLista: true,
    visitaConEnfermera: true,
  }),
});

/**
 * @param {string|null} institucionId lo que devuelve institucionTens
 * @returns {{pizarron: string[]|null, rotacionCupMin: number|null, ventanaEvolucionMin: number|null, reciboEnLista: true|null, visitaConEnfermera: true|null}}
 */
export function rasgosInstitucion(institucionId) {
  return RASGOS[institucionId] || SIN_RASGOS;
}

/**
 * ¿Es esto una cama? Es la MISMA regla de `sanitizarCama` (solo dígitos,
 * máximo 4) que repite como CHECK la tabla `turnos_bitacora`: el número es la
 * ubicación, jamás un identificador de la persona. `sanitizarCama` LIMPIA ("A3"
 * pasa a "3"); esto exige que el texto ya venga limpio, para que la pantalla
 * limpie mientras se escribe y valide lo que de verdad se va a guardar.
 * @param {string} txt
 * @returns {boolean}
 */
export function camaValida(txt) {
  const s = String(txt ?? '');
  return s !== '' && sanitizarCama(s) === s;
}
